import type { Puzzle } from "@shared/types.ts"
import type { PuzzleAction } from "../hooks/usePuzzleState.ts"
import type { CellClueInfo } from "../lib/grid-utils.ts"
import { useCallback, useState } from "react"
import { CrosswordGrid } from "./CrosswordGrid.tsx"
import { CluePanel } from "./CluePanel.tsx"
import { Toolbar } from "./Toolbar.tsx"
import { getWordCells } from "../lib/grid-utils.ts"
import { toggleFavorite } from "../lib/api-client.ts"
import { playPlaceSound, playDeleteSound } from "../lib/sounds.ts"

interface PuzzleViewProps {
  puzzle: Puzzle
  cells: Record<string, string>
  currentCell: [number, number] | null
  direction: "across" | "down"
  checkedCells: Record<string, "correct" | "incorrect">
  revealedCells: Set<string>
  flashCells: Record<string, "success" | "error">
  cellClueMap: Map<string, CellClueInfo>
  isComplete: boolean
  dispatch: React.Dispatch<PuzzleAction>
  onNewPuzzle?: () => void
  onHome: () => void
  onDelete?: () => void
  isFavorited?: boolean
  onToggleFavorite?: (favorited: boolean) => void
  slug?: string
}

export function PuzzleView({
  puzzle,
  cells,
  currentCell,
  direction,
  checkedCells,
  revealedCells,
  flashCells,
  cellClueMap,
  isComplete,
  dispatch,
  onNewPuzzle,
  onHome,
  onDelete,
  isFavorited = false,
  onToggleFavorite,
  slug,
}: PuzzleViewProps) {
  const [shareToast, setShareToast] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const handleCellClick = (row: number, col: number) => {
    if (puzzle.grid[row]![col]!.black) return

    if (currentCell && currentCell[0] === row && currentCell[1] === col) {
      dispatch({ type: "TOGGLE_DIRECTION" })
    } else {
      dispatch({ type: "SELECT_CELL", row, col })
    }
  }

  const handleClueClick = (number: number, dir: "across" | "down") => {
    const grid = puzzle.grid
    for (let r = 0; r < puzzle.gridSize; r++) {
      for (let c = 0; c < puzzle.gridSize; c++) {
        if (grid[r]![c]!.number === number) {
          const info = cellClueMap.get(`${r},${c}`)
          if (
            (dir === "across" && info?.across?.number === number) ||
            (dir === "down" && info?.down?.number === number)
          ) {
            dispatch({ type: "SELECT_CELL", row: r, col: c })
            dispatch({ type: "SET_DIRECTION", direction: dir })
            return
          }
        }
      }
    }
  }

  const triggerFlash = (
    cellList: [number, number][],
    checkCells: Record<string, string>,
    solution: string[][],
  ) => {
    const flashMap: Record<string, "success" | "error"> = {}
    let hasError = false
    let hasFilled = false
    for (const [r, c] of cellList) {
      const key = `${r},${c}`
      const userLetter = checkCells[key]
      if (userLetter) {
        hasFilled = true
        if (userLetter !== solution[r]![c]) hasError = true
      }
    }
    if (hasFilled) {
      const flashType = hasError ? "error" : "success"
      for (const [r, c] of cellList) flashMap[`${r},${c}`] = flashType
      dispatch({ type: "SET_FLASH", cells: flashMap })
      setTimeout(() => dispatch({ type: "CLEAR_FLASH" }), 600)
    }
  }

  const handleCheckWord = () => {
    if (!currentCell) return
    const wordCells = getWordCells(puzzle, currentCell[0], currentCell[1], direction, cellClueMap)
    dispatch({ type: "CHECK_WORD", wordCells, solution: puzzle.solution })
    triggerFlash(wordCells, cells, puzzle.solution)
  }

  const handleCheckAll = () => {
    dispatch({ type: "CHECK_ALL", solution: puzzle.solution })
    // Flash all filled cells
    const allCells: [number, number][] = []
    for (let r = 0; r < puzzle.gridSize; r++) {
      for (let c = 0; c < puzzle.gridSize; c++) {
        if (!puzzle.grid[r]![c]!.black && cells[`${r},${c}`]) {
          allCells.push([r, c])
        }
      }
    }
    triggerFlash(allCells, cells, puzzle.solution)
  }

  const handleRevealWord = () => {
    if (!currentCell) return
    const wordCells = getWordCells(puzzle, currentCell[0], currentCell[1], direction, cellClueMap)
    dispatch({ type: "REVEAL_WORD", wordCells, solution: puzzle.solution })
  }

  const handleClear = () => {
    if (!currentCell) return
    const wordCells = getWordCells(puzzle, currentCell[0], currentCell[1], direction, cellClueMap)
    for (const [r, c] of wordCells) {
      dispatch({ type: "CLEAR_CELL", row: r, col: c })
    }
  }

  // Mobile keyboard input handlers
  const findNextWhiteCell = useCallback(
    (row: number, col: number, dr: number, dc: number): [number, number] | null => {
      const size = puzzle.gridSize
      let r = row + dr
      let c = col + dc
      while (r >= 0 && r < size && c >= 0 && c < size) {
        if (!puzzle.grid[r]![c]!.black) return [r, c]
        r += dr
        c += dc
      }
      return null
    },
    [puzzle],
  )

  const handleLetterInput = useCallback(
    (letter: string) => {
      if (!currentCell) return
      const [row, col] = currentCell
      dispatch({ type: "SET_CELL", row, col, letter })
      playPlaceSound()

      const dr = direction === "down" ? 1 : 0
      const dc = direction === "across" ? 1 : 0
      const next = findNextWhiteCell(row, col, dr, dc)
      if (next) {
        const wordCells = getWordCells(puzzle, row, col, direction, cellClueMap)
        if (wordCells.some(([r, c]) => r === next[0] && c === next[1])) {
          dispatch({ type: "SELECT_CELL", row: next[0], col: next[1] })
        }
      }
    },
    [puzzle, currentCell, direction, cellClueMap, dispatch, findNextWhiteCell],
  )

  const handleBackspace = useCallback(() => {
    if (!currentCell) return
    const [row, col] = currentCell
    const key = `${row},${col}`

    if (cells[key]) {
      dispatch({ type: "CLEAR_CELL", row, col })
      playDeleteSound()
    }

    const dr = direction === "down" ? -1 : 0
    const dc = direction === "across" ? -1 : 0
    const prev = findNextWhiteCell(row, col, dr, dc)
    if (prev) {
      const wordCells = getWordCells(puzzle, row, col, direction, cellClueMap)
      if (wordCells.some(([r, c]) => r === prev[0] && c === prev[1])) {
        dispatch({ type: "SELECT_CELL", row: prev[0], col: prev[1] })
      }
    }
  }, [puzzle, currentCell, direction, cells, cellClueMap, dispatch, findNextWhiteCell])

  // Get current clue text for the header bar
  let currentClueText = ""
  if (currentCell) {
    const info = cellClueMap.get(`${currentCell[0]},${currentCell[1]}`)
    if (info) {
      const clueInfo = direction === "across" ? info.across : info.down
      if (clueInfo) {
        const clueList = direction === "across" ? puzzle.clues.across : puzzle.clues.down
        const clue = clueList.find((c) => c.number === clueInfo.number)
        if (clue) {
          const form = clue.displayAnswer ?? clue.answer
          const letterCount = form.includes(" ")
            ? `(${form.split(" ").map((w) => w.length).join(",")})`
            : `(${clue.length})`
          currentClueText = `${clue.number} ${direction.charAt(0).toUpperCase() + direction.slice(1)}: ${clue.clue} ${letterCount}`
        }
      }
    }
  }

  const handleToggleFavorite = useCallback(async () => {
    if (!slug || !onToggleFavorite) return
    // Optimistic update
    onToggleFavorite(!isFavorited)
    try {
      const result = await toggleFavorite(slug)
      onToggleFavorite(result.favorited)
    } catch {
      // Revert on error
      onToggleFavorite(isFavorited)
    }
  }, [slug, isFavorited, onToggleFavorite])

  const handleShare = useCallback(() => {
    if (!slug) return
    const url = `${window.location.origin}/p/${slug}`
    navigator.clipboard.writeText(url).then(() => {
      setShareToast(true)
      setTimeout(() => setShareToast(false), 2000)
    })
  }, [slug])

  return (
    <div className="puzzle-view">
      {isComplete && (
        <div className="completion-banner">
          Congratulations! Puzzle complete!
        </div>
      )}

      <div className="puzzle-header">
        <button className="home-brand" onClick={onHome}>cword</button>
        <h2>
          {puzzle.gridSize}x{puzzle.gridSize} {puzzle.puzzleType.charAt(0).toUpperCase() + puzzle.puzzleType.slice(1)} Crossword ({puzzle.gridStyle === "british" ? "British" : "American"})
        </h2>
        <div className="puzzle-header-actions">
          {onToggleFavorite && (
            <button
              className={`favorite-btn ${isFavorited ? "favorited" : ""}`}
              onClick={handleToggleFavorite}
              title={isFavorited ? "Remove from favorites" : "Add to favorites"}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill={isFavorited ? "currentColor" : "none"} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
              </svg>
            </button>
          )}
          {slug && (
            <button className="share-btn" onClick={handleShare} title="Copy link">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
              </svg>
              <span className="share-btn-text">Share</span>
            </button>
          )}
          {onDelete && !confirmDelete && (
            <button className="delete-btn" onClick={() => setConfirmDelete(true)} title="Delete puzzle">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="3 6 5 6 21 6" />
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
              </svg>
            </button>
          )}
          {onDelete && confirmDelete && (
            <span className="delete-confirm">
              <button className="delete-confirm-yes" onClick={onDelete}>Delete</button>
              <button className="delete-confirm-no" onClick={() => setConfirmDelete(false)}>Cancel</button>
            </span>
          )}
        </div>
      </div>

      {shareToast && (
        <div className="toast">Link copied to clipboard!</div>
      )}

      {currentClueText && (
        <div className="current-clue-bar">{currentClueText}</div>
      )}

      <div className="puzzle-layout">
        <div className="puzzle-grid-area">
          <CrosswordGrid
            puzzle={puzzle}
            cells={cells}
            currentCell={currentCell}
            direction={direction}
            checkedCells={checkedCells}
            revealedCells={revealedCells}
            flashCells={flashCells}
            cellClueMap={cellClueMap}
            onCellClick={handleCellClick}
            onLetterInput={handleLetterInput}
            onBackspace={handleBackspace}
          />
          {currentClueText && (
            <div className="current-clue-bar current-clue-bar-bottom">{currentClueText}</div>
          )}
          <Toolbar
            onCheckWord={handleCheckWord}
            onCheckAll={handleCheckAll}
            onRevealWord={handleRevealWord}
            onClear={handleClear}
            onNewPuzzle={onNewPuzzle}
          />
        </div>

        <CluePanel
          puzzle={puzzle}
          currentCell={currentCell}
          direction={direction}
          cellClueMap={cellClueMap}
          onClueClick={handleClueClick}
        />
      </div>
    </div>
  )
}
